/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import type { Map } from 'mapbox-gl';

export type MapErrorType = 'webgl' | 'map_error';

/** Animation options for a `recenter` call. */
export interface RecenterOptions {
    speed?: number;
    maxDuration?: number;
}

/**
 * Value exposed to descendants of `MapProvider`.
 *
 * - `mapInstance` is `null` until the map's style has fully loaded (the
 *   `styledata` event). Hooks that touch sources/layers must guard on this.
 * - `mapErrorType` is set to `'webgl'` when the browser lacks WebGL support
 *   or the map constructor throws, and to `'map_error'` when Mapbox emits an
 *   `error` event after init.
 * - `recenter` reframes the map using whichever camera-framing hook is
 *   mounted (e.g. `useFitBoundsToMarkers`), so siblings such as a "Recenter"
 *   button don't need to know which one it is. It's a no-op until that hook
 *   registers itself via `registerRecenter`.
 */
export interface MapContextValue {
    mapInstance: Map | null;
    /**
     * React 18's `MutableRefObject` shape — assignable to a JSX `ref` prop
     * because `.current` is writable. `RefObject<T | null>` (React 19 shape)
     * is not accepted by React 18's `LegacyRef`.
     */
    mapContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    mapErrorType: MapErrorType | null;
    recenter: (options?: RecenterOptions) => void;
    registerRecenter: (fn: (options?: RecenterOptions) => void) => void;
}

export const MapContext = React.createContext<MapContextValue | null>(null);

/**
 * Consume the surrounding `MapProvider`. Throws if called outside one so
 * misuse fails loudly at render time.
 */
export function useMap(): MapContextValue {
    const value = React.useContext(MapContext);
    if (!value) {
        throw new Error('useMap must be used inside a <MapProvider>');
    }
    return value;
}

// cSpell:ignore styledata
